import React, { FC } from 'react';
import { useAnimationOnScroll } from '@/Shared/Logic/Hooks/TechnicalHooks/useAnimationOnScroll';
import { PermissionFormValues, PermissionFormViewModel } from '../../Logic';
import { FaUserLock } from 'react-icons/fa';
import { AccessRightKey, AppEnum, CheckBoxField, DOMAIN_CONFIGS, DomainType } from '@/Shared';

interface PermissionFormRowCardProps {
	resourceKey: string;
	resourceLabel?: string
	entityFormVM: PermissionFormViewModel;
	values: PermissionFormValues;
	handleChange: (e: React.ChangeEvent<HTMLInputElement>, accessKey: string) => void
}


/**
 * =====================================================
 * Component: PermissionFormRowCard
 * =====================================================
 *
 * Purpose:
 * Renders a row card representing permissions for a single resource.
 * The row can either represent:
 *   1) A standard resource (e.g., Mandate, Contact)
 *   2) The global "All Resources" controller ("allDomain")
 *
 * Technical Notes:
 * - The component is purely UI-focused. It does not enforce any business rules
 *   about whether the current user actually has permission to modify these checkboxes.
 * - All state mutations are delegated to the parent via `handleChange`.
 * - `isAllDomain` mode aggregates child resource states to display the "all" checkboxes.
 * - The component uses a scroll animation hook to progressively reveal the card.
 *
 * Props:
 * - resourceKey: unique identifier of the resource
 * - resourceLabel: optional display label for the resource
 * - formVM: the view model providing access options and resource configuration
 * - values: current form values (Formik state)
 * - handleChange: callback to propagate checkbox changes upward
 *
 * Internal Helpers:
 * - getAllRecordsCheckBox(): renders "All Records" checkbox (data scope)
 * - getAccessCheckBox(): renders individual action checkboxes
 * - getAllOptionCheckBox(): renders "All Actions" checkbox (bulk action)
 *
 * Rendered Sections:
 * 1) Header: displays resource icon and label
 * 2) Body: displays all relevant checkboxes (All Records, All Actions, individual actions)
 */
const PermissionFormRowCard: FC<PermissionFormRowCardProps> = ({
	resourceKey,
	resourceLabel,
	entityFormVM,
	values,
	handleChange
}) => {

	/**
	 * Animation hook used to progressively reveal the card
	 * when it becomes visible in the viewport.
	 */
	const [ref, visible] = useAnimationOnScroll<HTMLDivElement>({}, true);

	/**
	 * Determines whether this row represents the global
	 * "All Resources" controller.
	 */
	const isAllDomain = resourceKey === 'allDomain';

	/**
	 * Constant representing the data-scope key.
	 * "allRecords" controls whether access applies to:
	 * - Only owned records
	 * - All records
	 */
	const allRecords = 'allRecords';

	/**
	 * Retrieves domain configuration (icon, labels, etc.)
	 * for standard resources.
	 */
	const {DomainIcon, domainLabelPl} = DOMAIN_CONFIGS?.[resourceKey as DomainType];


	/**
	 * =====================================================
	 * getAllRecordsCheckBox
	 * =====================================================
	 *
	 * Renders the "All Records" checkbox.
	 *
	 * Behavior:
	 * - In Global Mode:
	 *     The checkbox is checked only if ALL resources
	 *     have `allRecords = true`.
	 *
	 * - In Standard Mode:
	 *     The checkbox is bound directly to the specific
	 *     resource's `allRecords` value.
	 *
	 * Any change is delegated upward via `handleChange`.
	 */
	const getAllRecordsCheckBox = () => {
		if (isAllDomain) {

			return (
				<label className="CheckBoxField data-field-label" data-testid="CheckBoxField">
					<div className="form-group__checkbox">
						<input
							type='checkbox'
							name={`resourceAccessList.${resourceKey}.[${allRecords}]`}

							checked={
								entityFormVM.resourceOptions.every(resOpt =>
									values.resourceAccessList?.[resOpt.value]?.[allRecords] === true
								)
							}
							onChange={(event) => handleChange(event, allRecords)}
						/> Toutes Données
					</div>
				</label>
			);
		}

		return (
			<CheckBoxField
				name={`resourceAccessList.${resourceKey}.${allRecords}`}
				label='Toutes Données'
				onChange={(event) => handleChange(event, allRecords)}
			/>
		);
	}

	/**
	 * =====================================================
	 * getAccessCheckBox
	 * =====================================================
	 *
	 * Renders a checkbox for a specific access right
	 * (read, write, delete, restore, etc.).
	 *
	 * Behavior:
	 * - In Global Mode:
	 *     The checkbox is checked only if every resource
	 *     has the given access right enabled.
	 *
	 * - In Standard Mode:
	 *     The checkbox is directly bound to the specific
	 *     resource access value.
	 *
	 * Changes are propagated via `handleChange`.
	 */
	const getAccessCheckBox = (accessOpt: AppEnum) => {
		if (isAllDomain) {
			return <label className="CheckBoxField data-field-label" data-testid="CheckBoxField">
				<div className="form-group__checkbox">
					<input
						type='checkbox'
						name={`resourceAccessList.${resourceKey}.${accessOpt.value}`}

						checked={
							entityFormVM.resourceOptions.every(
								resOpt => values.resourceAccessList?.[resOpt.value]?.[accessOpt.value as AccessRightKey] === true
							)
						}
						onChange={(event) => handleChange(event, accessOpt.value as string)}
					/> {entityFormVM.getAccessRightLabel(accessOpt.value as string)}
				</div>
			</label>
		}
		return <CheckBoxField
			key={accessOpt.key}
			name={`resourceAccessList.${resourceKey}.${accessOpt.value}`}
			label={entityFormVM.getAccessRightLabel(accessOpt.value as string)}
		/>
	}


	/**
	 * =====================================================
	 * getAllOptionCheckBox
	 * =====================================================
	 *
	 * Renders the "All Actions" checkbox for this resource.
	 *
	 * Behavior:
	 * - In Global Mode:
	 *     Checked only if all resources have all access rights enabled.
	 *
	 * - In Standard Mode:
	 *     Checked if the specific resource has all access rights enabled.
	 *
	 * Changes propagate via `handleChange`.
	 */
	const getAllOptionCheckBox = () => {
		return (
		<label className="CheckBoxField data-field-label" data-testid="CheckBoxField">
			<div className="form-group__checkbox">
				<input
					type='checkbox'
					name={`resourceAccessList.${resourceKey}.all`}

					checked={
						isAllDomain ?
							entityFormVM.resourceOptions.every(resOpt => {
								return entityFormVM.accessOptions.every(accessOpt =>
									values.resourceAccessList?.[resOpt.value]?.[accessOpt.value as AccessRightKey] === true
								)
							})
							: entityFormVM.accessOptions.every(accessOpt =>
								values.resourceAccessList?.[resourceKey]?.[accessOpt.value as AccessRightKey] === true
							)
					}
					onChange={(event) => handleChange(event, 'all')}
				/> Toutes actions
			</div>
		</label>
		);
	}

	return (
		<div
			className={`PermissionFormRowCard DataRowCard`}
			data-testid="PermissionFormRowCard" ref={ref}
			style={{
				opacity: visible ? 1 : 0,
				transform: visible ? 'translateY(0)' : 'translateY(10px)',
				transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
				transitionDelay: `50ms`
			}}
		>
			{/* =====================================================
			    Header Section
			    Displays the domain icon and label.
			    Falls back to default values if not configured.
			===================================================== */}
			<div className='DataRowCard__header'>
				<span className='DataRowCard__header-main-icon layout_domain-icon'>
					{ DomainIcon ? <DomainIcon/> :<FaUserLock/>}
				</span>
				<div className='Rowcard-header-wrapper flex-row'>
					<div className='Rowcard-header__title ellipsis' title={resourceLabel ?? domainLabelPl ?? "Aucun label"}>
						{resourceLabel ?? domainLabelPl ?? "Aucun label"}
					</div>

				</div>
			</div>

			{/* =====================================================
			    Body Section
			    Renders:
			    - Data scope checkbox ("All Records")
			    - Bulk action checkbox ("All Actions")
			    - Individual access rights
			===================================================== */}
			<div className="DataRowCard__body">
				{getAllRecordsCheckBox()}
				{getAllOptionCheckBox()}
				{ entityFormVM.accessOptions.map(getAccessCheckBox)}
			</div>
		</div>
	);
}

export default PermissionFormRowCard;